/**
 * component-product-card-new
 *
 * Card-owned side drawer (full-width right panel on mobile, fixed-width right
 * panel on desktop) used by
 * the Bestsellers-style card. Both single- and multi-variant products open the
 * same drawer. The drawer fetches the product page and injects its
 * `<product-info>` (media, variant picker, quantity, cart form), then replaces
 * the theme's native subscription widget with a custom Subscribe vs One-time
 * buy-box (rendered from native selling-plan data in the card snippet). Add to
 * cart is handled by the global cart.js (which also toggles the button spinner),
 * and the cart drawer opens automatically on the resulting `cart:change` event.
 */

// Registers <product-info> so the drawer's variant re-fetch works on any page
// that renders this card (e.g. collection/landing pages, not just the PDP).
import './section-product.js';

function ensureAddButtonLabel(button, labelText, spinnerTemplate, addingLabel) {
  if (!button) return;

  let textEl = button.querySelector('[data-atc-text]');
  let spinner = button.querySelector('.loading__spinner');

  if (!textEl || !spinner) {
    button.replaceChildren();
    textEl = document.createElement('span');
    textEl.setAttribute('data-atc-text', '');
    button.appendChild(textEl);

    if (spinnerTemplate?.content?.firstElementChild) {
      spinner = spinnerTemplate.content.firstElementChild.cloneNode(true);
    } else {
      spinner = document.createElement('span');
      spinner.className = 'loading__spinner hidden';
    }
    button.appendChild(spinner);
  }

  textEl.textContent = labelText;
  if (addingLabel) button.dataset.addingLabel = addingLabel;
}

function setAddButtonLoading(button, isLoading, addingLabel) {
  if (!button) return;

  const textEl = button.querySelector('[data-atc-text]');
  const spinner = button.querySelector('.loading__spinner');

  if (isLoading) {
    if (textEl) {
      if (!textEl.dataset.originalText) {
        textEl.dataset.originalText = textEl.textContent;
      }
      // No hardcoded English fallback: addingLabel must come from Liquid via
      // a data attribute, same as ensureAddButtonLabel() below.
      textEl.textContent = button.dataset.addingLabel || addingLabel || textEl.dataset.originalText;
    }
    spinner?.classList.remove('hidden');
    button.disabled = true;
    return;
  }

  if (textEl?.dataset.originalText) {
    textEl.textContent = textEl.dataset.originalText;
    delete textEl.dataset.originalText;
  }
  spinner?.classList.add('hidden');
  button.disabled = false;
}

/* Drives the Subscribe / One-time buy-box: option toggle, delivery-frequency
   dropdown, per-variant repricing, the hidden selling_plan input, and the
   footer add button label. Powered by the per-variant JSON emitted by the
   card snippet. */
function formatFrequencyLabel(label) {
  if (!label) return '';
  return label
    .replace(/^deliver(?:y)?\s+every\s+/i, '')
    .replace(/^every\s+/i, '')
    .trim();
}
class BuyBox {
  constructor(root, dataMap, form, spinnerTemplate, labels) {
    this.root = root;
    this.dataMap = dataMap || {};
    this.form = form;
    this.spinnerTemplate = spinnerTemplate;
    this.addingLabel = labels?.adding;
    this.addToCartLabel = labels?.addToCart;
    this.addSubscriptionLabel = labels?.addSubscription;
    this.addToCartPlainLabel = labels?.addToCartPlain;
    this.addSubscriptionPlainLabel = labels?.addSubscriptionPlain;
    this.saveLabel = labels?.save;
    this.variantInput = form.querySelector('input[name="id"]');
    this.selected = 'subscribe';
    this.currentPlanId = null;

    this.subOption = root.querySelector('[data-bb-option="subscribe"]');
    this.otpOption = root.querySelector('[data-bb-option="otp"]');
    this.subRadio = this.subOption.querySelector('[data-bb-radio]');
    this.otpRadio = this.otpOption.querySelector('[data-bb-radio]');
    this.frequency = root.querySelector('[data-bb-frequency]');
    this.savePctEl = root.querySelector('[data-bb-savepct]');
    this.subPriceEl = root.querySelector('[data-bb-sub-price]');
    this.saveEl = root.querySelector('[data-bb-save]');
    this.otpPriceEl = root.querySelector('[data-bb-otp-price]');
    this.dropdown = root.querySelector('[data-bb-dropdown]');
    this.dropdownTrigger = root.querySelector('[data-bb-dropdown-trigger]');
    this.dropdownValue = root.querySelector('[data-bb-dropdown-value]');
    this.dropdownMenu = root.querySelector('[data-bb-dropdown-menu]');

    this.ensureSellingPlanInput();
    this.bindEvents();
    this.render();
  }

  ensureSellingPlanInput() {
    this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
    if (!this.sellingPlanInput) {
      this.sellingPlanInput = document.createElement('input');
      this.sellingPlanInput.type = 'hidden';
      this.sellingPlanInput.name = 'selling_plan';
      this.form.appendChild(this.sellingPlanInput);
    }
  }

  bindEvents() {
    this.subOption.addEventListener('click', () => this.select('subscribe'));
    this.otpOption.addEventListener('click', () => this.select('otp'));
    [this.subOption, this.otpOption].forEach((opt) => {
      opt.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.select(opt.dataset.bbOption);
        }
      });
    });

    this.dropdownTrigger.addEventListener('click', (event) => {
      event.stopPropagation();
      this.select('subscribe');
      this.toggleDropdown();
    });
    this.onDocumentClick = (event) => {
      if (!this.dropdown.contains(event.target)) this.closeDropdown();
    };
    document.addEventListener('click', this.onDocumentClick);

    // section-product.js updates input[name="id"] via the .value property (no
    // attribute mutation) and replaces the add-to-cart container after the
    // variant fetch. Observing that container's childList reliably fires once
    // the new variant id is already in place, so we can reprice.
    this.lastVariantId = this.variantInput?.value;
    const container = this.form.querySelector('[id^="add-to-cart-container"]');
    if (container) {
      this.observer = new MutationObserver(() => this.onVariantMaybeChanged());
      this.observer.observe(container, { childList: true });
    }
  }

  onVariantMaybeChanged() {
    const id = this.variantInput?.value;
    if (id === this.lastVariantId) return;
    this.lastVariantId = id;
    this.render();
  }

  get addButton() {
    return this.form.querySelector('button[name="add"], [type="submit"]');
  }

  get variantData() {
    const id = this.variantInput?.value;
    return id && this.dataMap[id] ? this.dataMap[id] : null;
  }

  get currentPlan() {
    return this.plans?.find((plan) => String(plan.id) === String(this.currentPlanId)) || this.plans?.[0] || null;
  }

  render() {
    const data = this.variantData;
    if (!data) {
      // Selected variant has no subscription option: hide the buy-box, fall back
      // to a plain one-time add.
      this.root.style.display = 'none';
      this.selected = 'otp';
      this.sellingPlanInput.value = '';
      this.sellingPlanInput.disabled = true;
      this.updateButton(false, null);
      return;
    }

    this.root.style.display = '';
    this.plans = data.plans || [];
    if (!this.plans.some((plan) => String(plan.id) === String(this.currentPlanId))) {
      this.currentPlanId = this.plans[0]?.id ?? null;
    }
    this.otpPriceEl.textContent = data.otpPrice;
    this.buildDropdown();
    this.renderSelectedPlan();
    this.applySelection();
  }

  buildDropdown() {
    this.dropdownMenu.innerHTML = '';
    this.plans.forEach((plan) => {
      const item = document.createElement('div');
      item.className = 'lp-bs__pc-dropdown-option';
      item.setAttribute('role', 'option');
      item.dataset.planId = plan.id;
      item.textContent = formatFrequencyLabel(plan.label);
      item.addEventListener('click', (event) => {
        event.stopPropagation();
        this.select('subscribe');
        this.currentPlanId = plan.id;
        this.renderSelectedPlan();
        this.applySelection();
        this.closeDropdown();
      });
      this.dropdownMenu.appendChild(item);
    });
    // A single frequency needs no dropdown affordance.
    this.dropdown.classList.toggle('lp-bs__pc-dropdown--static', this.plans.length <= 1);
  }

  renderSelectedPlan() {
    const plan = this.currentPlan;
    if (!plan) return;
    this.savePctEl.textContent = plan.savePct;
    this.subPriceEl.textContent = plan.price;
    this.saveEl.textContent = this.saveLabel ? this.saveLabel.replace('{{amount}}', plan.save) : plan.save;

    this.dropdownValue.textContent = formatFrequencyLabel(plan.label);
    this.dropdownMenu.querySelectorAll('.lp-bs__pc-dropdown-option').forEach((item) => {
      item.classList.toggle('lp-bs__pc-dropdown-option--selected', String(item.dataset.planId) === String(plan.id));
    });
  }

  select(type) {
    this.selected = type;
    this.applySelection();
  }

  applySelection() {
    const isSub = this.selected === 'subscribe' && this.plans?.length > 0;
    this.subOption.classList.toggle('is-active', isSub);
    this.otpOption.classList.toggle('is-active', !isSub);
    if (this.subRadio) this.subRadio.checked = isSub;
    if (this.otpRadio) this.otpRadio.checked = !isSub;
    if (this.frequency) this.frequency.style.display = isSub ? '' : 'none';
    this.root.dataset.bbSelected = isSub ? 'subscribe' : 'otp';

    this.sellingPlanInput.disabled = !isSub;
    this.sellingPlanInput.value = isSub ? (this.currentPlan?.id ?? '') : '';

    this.updateButton(isSub, isSub ? this.currentPlan?.price : this.variantData?.otpPrice);
  }

  updateButton(isSub, price) {
    const button = this.addButton;
    if (!button) return;
    const template = isSub ? this.addSubscriptionLabel : this.addToCartLabel;
    const plainLabel = isSub ? this.addSubscriptionPlainLabel : this.addToCartPlainLabel;
    const text = price && template ? template.replace('{{price}}', price) : plainLabel || template || '';
    ensureAddButtonLabel(button, text, this.spinnerTemplate, this.addingLabel);
  }

  toggleDropdown() {
    if (this.plans.length <= 1) return;
    const open = !this.dropdown.classList.contains('is-open');
    this.dropdown.classList.toggle('is-open', open);
    this.dropdownTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  closeDropdown() {
    this.dropdown.classList.remove('is-open');
    this.dropdownTrigger?.setAttribute('aria-expanded', 'false');
  }

  destroy() {
    this.observer?.disconnect();
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
  }
}

const CHEVRON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>';

const GALLERY_NAV_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.000297351 5.781L5.7798 11.562L6.8418 10.5015L2.1213 5.781L6.8418 1.062L5.7798 1.39265e-07L0.000297351 5.781Z" fill="black"/></svg>';

const FALLBACK_VARIATION_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5.75" fill="white" stroke="#CCCCCC" stroke-width="0.5"/></svg>';

/* Return the raw SVG for the first variant whose option at `optionIndex`
   matches `value`, or null. Mirrors the old theme's getVariationIcon. */
function getVariationIcon(optionIndex, value, variants) {
  if (!Array.isArray(variants)) return null;
  for (const variant of variants) {
    const matches =
      (optionIndex === 0 && variant.option1 === value) ||
      (optionIndex === 1 && variant.option2 === value) ||
      (optionIndex === 2 && variant.option3 === value);
    if (matches) return variant.variationIcon || null;
  }
  return null;
}

/* Old-theme-style variant picker for the drawer. Reads option groups from the
   hidden native <variant-selector>, renders custom dropdowns, and syncs the
   user's choice back to the native control (dispatching `change`) so the
   existing section-product.js variant re-fetch still runs. The native picker is
   re-rendered on each variant change, so we rebuild our UI when it mutates. */
class DrawerVariantSelector {
  constructor(variantSelector, bodyEl, onVariantChange, variantIcons, soldOutLabel) {
    this.variantSelector = variantSelector;
    this.bodyEl = bodyEl;
    this.onVariantChange = onVariantChange;
    this.variantIcons = variantIcons || [];
    this.soldOutLabel = soldOutLabel || '';
    this.product = this.readProduct();
    this.container = document.createElement('div');
    this.container.className = 'lp-bs__pc-variant';
    variantSelector.insertAdjacentElement('beforebegin', this.container);

    this.onDocumentClick = (event) => {
      if (!this.container.contains(event.target)) this.closeAll();
    };
    document.addEventListener('click', this.onDocumentClick);

    this.observer = new MutationObserver(() => this.render());
    this.observer.observe(variantSelector, { childList: true });

    this.render();
  }

  readProduct() {
    try {
      const script = this.bodyEl?.querySelector('script[data-product]');
      return script ? JSON.parse(script.textContent) : null;
    } catch (error) {
      console.error('DrawerVariantSelector: invalid product data', error);
      return null;
    }
  }

  getOptionGroups() {
    const vs = this.variantSelector;
    const groups = [];
    vs.querySelectorAll('fieldset.product-form__input').forEach((fieldset) => {
      const select = fieldset.querySelector('select');
      if (select) {
        const name = vs.querySelector(`label[for="${select.id}"]`)?.textContent.trim() || '';
        const values = Array.from(select.options).map((opt) => ({
          el: opt,
          value: opt.value,
          label: opt.textContent.replace(/\bUnavailable\b/i, '').trim(),
          available: !/\bUnavailable\b/i.test(opt.textContent),
          selected: opt.selected,
        }));
        groups.push({ type: 'select', control: select, name, values });
        return;
      }
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (!radios.length) return;
      const name = (fieldset.querySelector('legend')?.textContent.trim() || '').replace(/:\s*$/, '');
      const values = radios.map((radio) => ({
        el: radio,
        value: radio.value,
        label: vs.querySelector(`label[for="${radio.id}"]`)?.textContent.trim() || radio.value,
        available: !radio.classList.contains('disabled'),
        selected: radio.checked,
      }));
      groups.push({ type: 'radio', control: fieldset, name, values });
    });
    return groups;
  }

  render() {
    const groups = this.getOptionGroups();
    this.container.innerHTML = '';
    if (!groups.length) {
      this.container.style.display = 'none';
      return;
    }
    this.container.style.display = '';
    for (const [optionIndex, group] of groups.entries()) {
      this.container.appendChild(this.buildOption(group, optionIndex));
    }
  }

  buildOption(group, optionIndex) {
    const selected = group.values.find((value) => value.selected) || group.values[0];

    const option = document.createElement('div');
    option.className = 'lp-bs__pc-option';

    const label = document.createElement('label');
    label.className = 'lp-bs__pc-option-name';
    label.textContent = `${group.name.replace(/:\s*$/, '')} (${group.values.length})`;
    option.appendChild(label);

    const dropdown = document.createElement('div');
    dropdown.className = 'lp-bs__pc-dropdown';

    const trigger = document.createElement('div');
    trigger.className = 'lp-bs__pc-dropdown-selected';
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.appendChild(this.buildValueInner(selected, false, optionIndex));
    trigger.insertAdjacentHTML('beforeend', CHEVRON_SVG);

    const menu = document.createElement('div');
    menu.className = 'lp-bs__pc-dropdown-options';
    menu.setAttribute('role', 'listbox');

    const openDropdown = () => {
      this.closeAll();
      dropdown.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    };

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = dropdown.classList.contains('is-open');
      this.closeAll();
      if (!isOpen) openDropdown();
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (dropdown.classList.contains('is-open')) this.closeAll();
        else openDropdown();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        openDropdown();
        menu.querySelector('.lp-bs__pc-dropdown-option')?.focus();
      } else if (event.key === 'Escape' && dropdown.classList.contains('is-open')) {
        event.preventDefault();
        this.closeAll();
      }
    });
    group.values.forEach((value) => {
      const item = document.createElement('div');
      item.className = 'lp-bs__pc-dropdown-option';
      item.setAttribute('role', 'option');
      item.setAttribute('tabindex', '-1');
      if (value.selected) item.classList.add('lp-bs__pc-dropdown-option--selected');
      item.appendChild(this.buildValueInner(value, true, optionIndex));
      item.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeAll();
        this.selectValue(group, value);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.closeAll();
          trigger.focus();
          this.selectValue(group, value);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.closeAll();
          trigger.focus();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const items = Array.from(menu.querySelectorAll('.lp-bs__pc-dropdown-option'));
          const next = items[items.indexOf(item) + (event.key === 'ArrowDown' ? 1 : -1)];
          next?.focus();
        }
      });
      menu.appendChild(item);
    });

    dropdown.appendChild(trigger);
    dropdown.appendChild(menu);
    option.appendChild(dropdown);
    return option;
  }

  buildValueInner(value, plain, optionIndex) {
    const wrapper = document.createElement(plain ? 'span' : 'div');
    if (!plain) wrapper.className = 'lp-bs__pc-dropdown-selected-inner';
    const icon = document.createElement('span');
    icon.className = 'lp-bs__pc-variation-icon';
    icon.innerHTML = getVariationIcon(optionIndex, value?.value, this.variantIcons) || FALLBACK_VARIATION_ICON_SVG;
    wrapper.appendChild(icon);
    const text = document.createElement('span');
    text.textContent = value?.label || '';
    wrapper.appendChild(text);
    if (value && !value.available) {
      const sold = document.createElement('span');
      sold.className = 'sold-out-label';
      sold.textContent = this.soldOutLabel;
      wrapper.appendChild(sold);
    }
    return wrapper;
  }

  selectValue(group, value) {
    if (value.selected) return;
    if (group.type === 'select') {
      group.control.value = value.value;
      group.control.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      value.el.checked = true;
      value.el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // setupFooter moves the cart form out of <product-info>, so the native
    // section-product.js re-fetch can no longer update input[name="id"] or the
    // add button. Resolve and apply the selected variant ourselves, straight
    // away, so the correct variant is what gets added.
    this.syncVariantId();
    // Reflect the new selection immediately; the native re-fetch still updates
    // the (hidden) native picker, which triggers another render via the
    // observer once availability data comes back.
    this.render();
  }

  /* Match the currently selected option values against the product's variants
     and apply the resolved variant to every cart form input and the add button
     synchronously, independent of the native re-fetch. */
  syncVariantId() {
    if (!this.product?.variants) return;
    const selected = this.getOptionGroups().map((group) => {
      const value = group.values.find((entry) => entry.selected) || group.values[0];
      return value?.value;
    });
    const match = this.product.variants.find((variant) => selected.every((val, index) => variant.options[index] === val));
    if (match) {
      this.bodyEl?.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = match.id;
      });
    }
    const addButton = this.bodyEl?.querySelector('.product-form__buttons button[name="add"], .product-form__buttons [type="submit"]');
    if (addButton) addButton.disabled = !match || !match.available;
    // Let the buy-box (if any) reprice for the new variant.
    this.onVariantChange?.();
  }

  closeAll() {
    this.container.querySelectorAll('.lp-bs__pc-dropdown.is-open').forEach((dropdown) => {
      dropdown.classList.remove('is-open');
      dropdown.querySelector('.lp-bs__pc-dropdown-selected')?.setAttribute('aria-expanded', 'false');
    });
  }

  destroy() {
    this.observer?.disconnect();
    document.removeEventListener('click', this.onDocumentClick);
  }
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/* Controls inside the card that keep their own click behaviour. The Swiper
   arrows are divs, so they have to be named here. */
const CARD_CONTROLS =
  'a, button, input, select, textarea, label, summary, .product-card-swiper-button-next, .product-card-swiper-button-prev';

class ProductCardNewDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.hasLoaded = false;
    this.onKeydown = this.onKeydown.bind(this);
    this.onCartChange = this.onCartChange.bind(this);
    this.onCardClick = this.onCardClick.bind(this);
  }

  connectedCallback() {
    this.setupCardLink();
    this.setupCardSwiper();

    // Single-variant products with no subscription option skip the drawer and
    // add straight to cart. `directAddBound` (not `initialized`) guards this
    // listener so it survives a disconnect/reconnect (Swiper re-parenting,
    // a Shopify section re-render) without ever being attached twice.
    this.directAddButton = this.directAddButton || this.querySelector('[data-direct-add]');
    if (this.directAddButton) {
      if (!this.directAddBound) {
        this.directAddButton.addEventListener('click', () => this.addDirect());
        this.directAddBound = true;
      }
      return;
    }

    // One-time DOM setup: querying elements, parsing buy-box data, and wiring
    // the open/overlay/close buttons only ever needs to happen once for the
    // lifetime of these DOM nodes, so it stays behind `initialized`.
    if (!this.initialized) {
      this.openButton = this.querySelector('[data-drawer-open]');
      this.drawerRoot = this.querySelector('[data-drawer-root]');
      if (!this.openButton || !this.drawerRoot) return;

      this.overlay = this.drawerRoot.querySelector('[data-drawer-overlay]');
      this.drawer = this.drawerRoot.querySelector('[data-drawer]');
      this.body = this.drawerRoot.querySelector('[data-drawer-body]');
      this.productUrl = this.dataset.productUrl;

      this.buyboxTemplate = this.querySelector('template[data-buybox]');
      const dataScript = this.querySelector('[data-buybox-data]');
      try {
        this.buyboxData = dataScript ? JSON.parse(dataScript.textContent) : null;
      } catch (error) {
        this.buyboxData = null;
        console.error('ProductCardNewDrawer: invalid buy-box data', error);
      }

      this.openButton.addEventListener('click', () => this.open());
      this.overlay?.addEventListener('click', () => this.close());
      this.drawerRoot.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close());

      this.initialized = true;
    }

    // Re-parenting and document-level listeners don't survive a disconnect —
    // restore them on every connection, not just the first.
    document.body.appendChild(this.drawerRoot);
    document.addEventListener('cart:change', this.onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('keydown', this.onKeydown);
    this.cardSwiper?.destroy(true, true);
    this.cardSwiper = null;
    // variantUI/buyBox are left intact: they're bound to elements inside
    // this.body, which stays populated across a disconnect (only drawerRoot's
    // position in the document changes), so nothing needs rebuilding on
    // reconnect and destroying them here would just force a needless refetch.
    this.drawerRoot?.remove();
  }

  /* Pointer shortcut for the whole tile. The title link is the one control
     that is announced and tabbable, so this adds nothing to the tab order; it
     only gives the rest of the tile the same destination. addEventListener
     ignores a duplicate listener, so a reconnect can't attach it twice. */
  setupCardLink() {
    this.addEventListener('click', this.onCardClick);
  }

  /**
   * Navigates to the title link's destination. A synthetic click on the link
   * would not reliably open a new tab for a modified click, so the modifier
   * cases are handled explicitly and the link supplies the URL and target.
   * Swiper cancels the click that ends a swipe, which surfaces here as
   * `defaultPrevented`, so dragging the image gallery never navigates.
   * @param {MouseEvent} event
   */
  onCardClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.target.closest(CARD_CONTROLS)) return;
    if (window.getSelection()?.toString()) return;

    const link = this.querySelector('.lp-bs__title-link');
    if (!link?.href) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || link.target === '_blank') {
      window.open(link.href, '_blank', 'noopener');
      return;
    }

    window.location.assign(link.href);
  }

  /* Card-face image gallery. Mirrors LpBestsellers' Swiper guard
     (section-military-discount-section-5.js): retry after `load` if the
     library hasn't parsed yet, and skip re-init on a reconnect that didn't
     tear the slider down first. */
  setupCardSwiper() {
    if (this.cardSwiper) return;
    const slider = this.querySelector('.product-card-swiper');
    if (!slider) return;

    const SwiperClass = window.Swiper;
    if (typeof SwiperClass === 'undefined') {
      window.addEventListener('load', () => this.setupCardSwiper(), { once: true });
      return;
    }
    if (slider.classList.contains('swiper-initialized')) return;

    this.cardSwiper = new SwiperClass(slider, {
      slidesPerView: 1,
      loop: true,
      navigation: {
        nextEl: slider.querySelector('.product-card-swiper-button-next'),
        prevEl: slider.querySelector('.product-card-swiper-button-prev'),
      },
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  /**
   * Keeps Tab inside the drawer while it is open.
   * @param {KeyboardEvent} event
   */
  trapFocus(event) {
    const focusable = [...this.drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onCartChange(event) {
    if (this.isOpen && event.detail?.action === 'add') this.close();
  }

  /* Single-variant, no-subscription add path: post straight to the cart engine
     (which opens the cart drawer on the resulting `cart:change`) instead of
     opening the options drawer. */
  addDirect() {
    const variantId = this.directAddButton.dataset.variantId;
    if (!variantId || !window.Cart || this.directAddButton.disabled) return;

    setAddButtonLoading(this.directAddButton, true, this.dataset.addingLabel);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    window.Cart.add(formData, this)
      .catch((error) => console.error('ProductCardNewDrawer: direct add failed', error))
      .finally(() => {
        setAddButtonLoading(this.directAddButton, false);
      });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.drawerRoot.hidden = false;
    // Force reflow so the transition runs from the hidden state.
    void this.drawerRoot.offsetWidth;
    this.drawerRoot.classList.add('is-open');
    this.drawer?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lp-bs-drawer-open');
    document.addEventListener('keydown', this.onKeydown);
    this.drawerRoot.querySelector('[data-drawer-close]')?.focus();

    if (!this.hasLoaded) this.loadContent();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.drawerRoot.classList.remove('is-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lp-bs-drawer-open');
    document.removeEventListener('keydown', this.onKeydown);
    this.openButton?.focus();

    // Hide after the transition so it can animate out.
    window.setTimeout(() => {
      if (!this.isOpen) this.drawerRoot.hidden = true;
    }, 350);
  }

  showLoader() {
    this.body.innerHTML =
      '<div class="lp-bs__drawer-loader" role="status" aria-live="polite"><span class="lp-bs__drawer-spinner"></span></div>';
  }

  loadContent() {
    if (!this.productUrl) return;
    this.showLoader();

    fetch(this.productUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const productElement = new DOMParser().parseFromString(responseText, 'text/html').querySelector('product-info');

        if (!productElement) {
          this.body.innerHTML = '';
          console.error('ProductCardNewDrawer: product-info not found in response');
          return;
        }

        this.preprocessContent(productElement);
        this.setContent(productElement.outerHTML);
        this.injectTitle();
        this.setupVariantSelector();
        this.setupBuyBox();
        this.setupFooter();
        this.setupGalleryNavigation();
        this.hasLoaded = true;

        if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
      })
      .catch((error) => {
        this.body.innerHTML = '';
        console.error('ProductCardNewDrawer: error loading product', error);
      });
  }

  /* Mirrors component-quick-add.js: namespace the section with a `-modal-`
     suffix (so section-product.js can recover the real section id on variant
     re-fetch), keep it from rewriting the URL, remove the native subscription
     widget (replaced by our buy-box), and drop pieces that don't belong here. */
  preprocessContent(element) {
    const uniqueSectionId = `${element.dataset.section}-modal-${Date.now()}`;
    element.innerHTML = element.innerHTML.replaceAll(element.dataset.section, uniqueSectionId);
    element.dataset.section = uniqueSectionId;
    element.dataset.updateUrl = 'false';
    element.querySelector('.product__subscription')?.remove();
    element.querySelector('pickup-availability')?.remove();
    element.querySelector('script[src*="component-pickup-availability.js"]')?.remove();
    element.querySelector('product-media-modal')?.remove();
    element.querySelector('script[src*="component-product-media-modal.js"]')?.remove();
    element.querySelector('product-recommendations')?.remove();
    element.querySelector('script[src*="product-recommendations.js"]')?.remove();
  }

  setContent(html) {
    this.body.innerHTML = html;
    // Re-create scripts so the browser executes them (innerHTML scripts don't run).
    this.body.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /* Inject the product title at the top of the drawer body (the native PDP
     title is hidden), mirroring the old drawer layout. */
  injectTitle() {
    const product = this.body.querySelector('.product') || this.body.querySelector('product-info');
    if (!product || product.querySelector('.lp-bs__pc-title')) return;
    const title = document.createElement('h2');
    title.className = 'lp-bs__pc-title';
    title.textContent = this.dataset.productTitle || '';
    product.insertBefore(title, product.firstChild);
  }

  /* Hide the native PDP variant picker (kept in the DOM for the cart form +
     section-product.js re-fetch) and build the old-theme-style custom
     dropdowns that drive it. */
  setupVariantSelector() {
    const variantSelector = this.body.querySelector('variant-selector');
    if (!variantSelector) return;
    let variantIcons = [];
    const iconScript = this.querySelector('script[data-variant-icons]');
    if (iconScript) {
      try {
        variantIcons = JSON.parse(iconScript.textContent);
      } catch (error) {
        console.error('ProductCardNewDrawer: invalid variant icon data', error);
      }
    }
    this.variantUI = new DrawerVariantSelector(
      variantSelector,
      this.body,
      () => this.buyBox?.render(),
      variantIcons,
      this.dataset.labelSoldOut,
    );
  }

  /* Move the buy buttons (with their form) into a sticky actions footer and add
     the details link, mirroring the old `bpn-product-content__actions`. */
  setupFooter() {
    const buttons = this.body.querySelector('.product-form__buttons');
    if (!buttons) return;

    let actions = this.body.querySelector('.lp-bs__pc-actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'lp-bs__pc-actions';
      this.body.appendChild(actions);
    }
    actions.appendChild(buttons);

    if (!actions.querySelector('.lp-bs__pc-view-details')) {
      const link = document.createElement('a');
      link.className = 'lp-bs__pc-view-details';
      link.href = this.productUrl;
      link.textContent = this.dataset.labelViewDetails || '';
      actions.appendChild(link);
    }
  }

  /* Overlay prev/next arrows on the main media gallery, wired to the Swiper
     instance created by ProductInfo.connectedCallback(). Mirrors the old
     `bpn-product-content__images-nav-btn` overlay. */
  setupGalleryNavigation() {
    const productInfo = this.body.querySelector('product-info');
    const main = productInfo?.querySelector('.product-media-gallery__main');
    if (!main) return;

    const slides = main.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;
    if (main.querySelector('.lp-bs__pc-gallery-nav')) return;

    const swiper = productInfo.swiper;

    const makeButton = (direction) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `lp-bs__pc-gallery-nav lp-bs__pc-gallery-nav--${direction}`;
      button.setAttribute('aria-label', direction === 'prev' ? this.dataset.labelPrevImage : this.dataset.labelNextImage);
      button.innerHTML = GALLERY_NAV_SVG;
      button.addEventListener('click', () => {
        if (direction === 'prev') swiper?.slidePrev();
        else swiper?.slideNext();
      });
      return button;
    };

    const prevButton = makeButton('prev');
    const nextButton = makeButton('next');
    main.appendChild(prevButton);
    main.appendChild(nextButton);

    if (swiper && !swiper.params?.loop) {
      const updateDisabled = () => {
        prevButton.disabled = swiper.isBeginning;
        nextButton.disabled = swiper.isEnd;
      };
      swiper.on('slideChange', updateDisabled);
      updateDisabled();
    }
  }

  /* Insert the card-rendered buy-box just below the variant picker and wire it
     up. Products with no selling plans keep the standard add-to-cart button. */
  setupBuyBox() {
    const addButton = this.body.querySelector('.product-form__buttons button[name="add"], .product-form__buttons [type="submit"]');
    const form = addButton?.closest('form') || this.body.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    if (!this.buyboxTemplate || !this.buyboxData) {
      this.setSimpleButtonLabel();
      return;
    }

    const root = this.buyboxTemplate.content.firstElementChild.cloneNode(true);
    const variants = this.body.querySelector('.product__variants');
    const quantity = this.body.querySelector('.product__quantity');
    const wrapper = this.body.querySelector('.product__info-wrapper');

    if (variants) variants.insertAdjacentElement('afterend', root);
    else if (quantity) quantity.insertAdjacentElement('beforebegin', root);
    else if (wrapper) wrapper.appendChild(root);
    else form.insertAdjacentElement('beforebegin', root);

    this.buyBox = new BuyBox(root, this.buyboxData, form, this.querySelector('template[data-atc-spinner]'), {
      adding: this.dataset.addingLabel,
      addToCart: this.dataset.labelAddToCart,
      addSubscription: this.dataset.labelAddSubscription,
      addToCartPlain: this.dataset.labelAddToCartPlain,
      addSubscriptionPlain: this.dataset.labelAddSubscriptionPlain,
      save: this.dataset.labelSave,
    });
  }

  setSimpleButtonLabel() {
    const button = this.body.querySelector('.product-form__buttons button[name="add"], .product-form__buttons [type="submit"]');
    if (!button) return;
    const priceEl = this.body.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular, .price .price-item');
    const price = priceEl?.textContent.trim();
    const template = this.dataset.labelAddToCart;
    const text = price && template ? template.replace('{{price}}', price) : this.dataset.labelAddToCartPlain || template || '';
    ensureAddButtonLabel(button, text, this.querySelector('template[data-atc-spinner]'), this.dataset.addingLabel);
  }
}

if (!customElements.get('product-card-new-drawer')) {
  customElements.define('product-card-new-drawer', ProductCardNewDrawer);
}
